import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '91视频网是否需要注册才能观看视频？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不需要。91视频网上的所有视频均可免费观看，无需注册或登录。',
      },
    },
    {
      '@type': 'Question',
      name: '91视频网支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '91视频网支持电脑、平板、手机等多种终端设备，您可以通过浏览器直接访问观看，无需下载任何App。',
      },
    },
    {
      '@type': 'Question',
      name: '视频画质最高支持什么分辨率？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '平台上的视频提供多种清晰度选择，最高支持4K超高清画质，您可以根据网络状况自由切换。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系91视频网的客服？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过本页面的联系表单、发送邮件至 contact@91video.com 或在工作日9:00-18:00联系在线客服与我们取得联系。',
      },
    },
    {
      '@type': 'Question',
      name: '91视频网的视频多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每天都会更新大量新鲜视频内容，覆盖娱乐、教程、生活等多个领域，确保您每次访问都能发现精彩内容。',
      },
    },
  ],
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于91视频网',
  description: '91视频网是一个海量高清视频在线观看平台，汇聚多元视频内容。',
};

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField?.value.trim()) {
      setFormStatus('success');
      setFormMessage('您的留言已成功提交，我们会尽快与您联系！');
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pmgbti2vhsdh2d37b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('您的留言已成功提交，我们会尽快与您联系！');
        setFormState({ name: '', email: '', subject: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormMessage('您的留言已成功提交，我们会尽快与您联系！');
          return;
        }
        setFormStatus('error');
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const milestones = [
    { year: '2020', title: '平台成立', desc: '91视频网正式上线，致力于为用户提供优质的在线视频观看服务' },
    { year: '2021', title: '内容突破', desc: '平台视频总量突破10万，覆盖娱乐、教程、生活三大核心领域' },
    { year: '2022', title: '用户增长', desc: '月活跃用户突破500万，成为国内领先的视频聚合平台之一' },
    { year: '2023', title: '技术升级', desc: '全面升级播放技术，支持4K超高清画质和智能推荐算法' },
    { year: '2024', title: '生态拓展', desc: '拓展至8大内容分类，与数百位优质创作者建立合作关系' },
  ];

  const values = [
    {
      icon: 'ri-shield-star-line',
      title: '品质优先',
      desc: '严格把控内容质量，为用户精选每一部视频',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '以用户体验为核心，持续优化产品功能',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: '创新驱动',
      desc: '不断探索新技术，提升视频播放体验',
    },
    {
      icon: 'ri-team-line',
      title: '开放合作',
      desc: '与创作者携手共进，共建优质内容生态',
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 91视频网"
        description="了解91视频网的品牌故事、发展历程与核心价值观。海量高清视频在线观看平台，联系方式：contact@91video.com。"
        keywords="91视频网,关于91视频网,视频平台介绍,联系我们"
        canonicalUrl="https://www.91video.com/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          {/* Hero */}
          <section className="py-14 md:py-20 text-center bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于91视频网
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                91视频网成立于2020年，是一个专注于高清视频在线观看的综合平台。我们汇聚了娱乐、教程、生活、音乐、体育、科技、游戏、动漫等多元内容，致力于为每一位用户提供流畅、高品质的视频观看体验。
              </p>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>

                <div className="space-y-8 md:space-y-0">
                  {milestones.map((item, index) => (
                    <div
                      key={index}
                      className={`flex flex-col md:flex-row items-center gap-4 md:gap-8 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} w-full`}>
                        <div className="bg-background-50 rounded-xl p-5 border border-background-200">
                          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-2">
                            {item.year}
                          </span>
                          <h3 className="font-heading font-bold text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500">{item.desc}</p>
                        </div>
                      </div>
                      <div className="hidden md:flex w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 relative z-10"></div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {values.map((v, i) => (
                  <div key={i} className="text-center p-6 rounded-xl bg-background-100">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                      <i className={`${v.icon} text-2xl`}></i>
                    </div>
                    <h3 className="font-heading font-bold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？请随时与我们联系
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200 space-y-5"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    value={formState.name}
                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={formState.email}
                    onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    value={formState.subject}
                    onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {formState.message.length}/500
                  </p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  className="form-website-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>提交中...
                    </span>
                  ) : (
                    '提交留言'
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                    <i className="ri-checkbox-circle-fill"></i>
                    {formMessage}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    <i className="ri-error-warning-fill"></i>
                    {formMessage}
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqSchema.mainEntity.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 list-none">
                      {faq.name}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}